/**
 * Hotel Details API
 *
 * Network-only functions for hotel details operations.
 * NO Redux imports allowed in this file.
 */
import type { HotelDetailsTemplate } from '@contracts';
import { HOTELS_ENDPOINTS } from '@/api/endpoints';
import { buildHeaders, buildUrl, buildQueryParams } from '@/api/utils';
import {
  generateUUID,
  getCookie,
  getUserUUID,
  AUTH_COOKIE_NAME,
  getOrCreateSessionId,
} from '@/shared/utils';
import { logger } from '@/shared/utils/logger';

// ============================================
// Types
// ============================================

/**
 * Device information for API requests
 */
export interface DeviceInfo {
  device_id: string;
  app_ver: string;
  device_type?: string;
  platform?: string;
}

/**
 * Location context for hotel details request
 */
export interface LocationContext {
  city?: string;
  city_code?: string;
  country?: string;
  area?: string;
  poi?: string;
  hotel?: string;
  hotel_id?: string;
}

/**
 * Parameters for fetching hotel details
 */
export interface HotelDetailsRequestParams {
  hotelId: string;
  hotelName: string;
  city?: string;
  cityCode?: string;
  checkin?: string;
  checkout?: string;
  adultCount?: number;
  childCount?: number;
  childrenAges?: number[];
  roomCount?: number;
  currency?: string;
  deviceInfo?: DeviceInfo;
  /**
   * UUIDs for subsequent calls to fetch weaverResponse and hotelCompareResponse.
   * When provided, the API uses HOTEL_STATIC_DETAIL identifier instead of HOTEL_DETAILS_AND_SEARCH_ROOMS.
   */
  uuids?: string[];
}

/**
 * Custom error for hotel details API failures
 */
export class HotelDetailsError extends Error {
  constructor(
    message: string,
    public readonly statusCode?: number,
    public readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'HotelDetailsError';
  }
}

// ============================================
// Utility Functions
// ============================================

/**
 * Get or create device ID from localStorage
 */
function getOrCreateDeviceId(): string {
  const STORAGE_KEY = 'mmt_device_id';
  if (typeof window === 'undefined') {
    return generateUUID();
  }

  const existing = localStorage.getItem(STORAGE_KEY);
  if (existing) {
    return existing;
  }

  const newDeviceId = generateUUID();
  localStorage.setItem(STORAGE_KEY, newDeviceId);
  return newDeviceId;
}

/**
 * Get default device info
 */
function getDefaultDeviceInfo(): DeviceInfo {
  return {
    device_id: getOrCreateDeviceId(),
    app_ver: '1.21.1',
    device_type: 'DESKTOP',
    platform: 'DESKTOP',
  };
}

/**
 * Get default check-in date (7 days from today)
 */
function getDefaultCheckin(): string {
  const date = new Date();
  date.setDate(date.getDate() + 7);
  return date.toISOString().split('T')[0] ?? '';
}

/**
 * Get default check-out date (1 day after check-in)
 */
function getDefaultCheckout(checkin?: string): string {
  const checkinDate = checkin ? new Date(checkin) : getDefaultCheckin();
  const checkoutDate = new Date(checkinDate);
  checkoutDate.setDate(checkoutDate.getDate() + 1);
  return checkoutDate.toISOString().split('T')[0] ?? '';
}

/**
 * Build request body for hotel details API
 */
function buildRequestBody(params: HotelDetailsRequestParams): unknown {
  const requestId = generateUUID();
  const conversationId = getOrCreateSessionId();
  const uuid = getUserUUID();

  const auth = getCookie(AUTH_COOKIE_NAME);

  const checkin = params.checkin ?? getDefaultCheckin();
  const checkout = params.checkout ?? getDefaultCheckout(checkin);
  const deviceInfo = params.deviceInfo ?? getDefaultDeviceInfo();

  const locationContext: LocationContext = {
    ...(params.city && { city: params.city }),
    ...(params.cityCode && { city_code: params.cityCode }),
    hotel: params.hotelName,
    hotel_id: params.hotelId,
  };

  // When UUIDs are provided, use HOTEL_STATIC_DETAIL intent for subsequent calls
  const hasUuids = params.uuids && params.uuids.length > 0;
  const intents = hasUuids ? ['HOTEL_STATIC_DETAIL'] : ['Detail', 'HOTEL_DETAIL'];

  return {
    request_id: requestId,
    conversation_id: conversationId,
    uuid,
    auth,
    device_info: deviceInfo,
    rephrased_query: `Show me details about ${params.hotelName}`,
    intents,
    location_context: locationContext,
    checkin,
    checkout,
    adult_count: params.adultCount ?? 2,
    room_count: params.roomCount ?? 1,
    child_count: params.childCount ?? 0,
    children_ages: params.childrenAges ?? [],
    currency: params.currency ?? 'INR',
    uuids: params.uuids ?? [],
  };
}

// ============================================
// API Functions
// ============================================

/**
 * Fetch hotel details from the API
 */
export async function fetchHotelDetails(
  params: HotelDetailsRequestParams,
): Promise<HotelDetailsTemplate> {
  const url = buildUrl(HOTELS_ENDPOINTS.DETAILS, buildQueryParams());
  const requestBody = buildRequestBody(params);

  // logger.warn('[HotelDetailsClient] Fetching hotel details:', {
  //   hotelId: params.hotelId,
  //   hotelName: params.hotelName,
  //   city: params.city,
  // });

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify(requestBody),
    });

    let responseData: unknown;
    try {
      responseData = await response.json();
    } catch {
      const text = await response.text();
      responseData = text;
    }

    if (!response.ok) {
      logger.error('[HotelDetailsClient] API error response:', {
        status: response.status,
        statusText: response.statusText,
        body: responseData,
      });
      throw new HotelDetailsError(
        `API request failed: ${response.status} ${response.statusText}`,
        response.status,
        responseData,
      );
    }

    if (
      typeof responseData !== 'object' ||
      responseData === null ||
      !('type' in responseData) ||
      !('data' in responseData)
    ) {
      logger.error('[HotelDetailsClient] Invalid response structure:', responseData);
      throw new HotelDetailsError(
        'Invalid response structure: Expected template with type and data fields',
        500,
        responseData,
      );
    }

    const template = responseData as HotelDetailsTemplate;

    if (template.type !== 'hotels-details') {
      logger.error('[HotelDetailsClient] Invalid template type:', template.type);
      throw new HotelDetailsError(
        `Invalid template type: Expected 'hotels-details', got '${template.type}'`,
        500,
        template,
      );
    }

    if (!template.data) {
      logger.error('[HotelDetailsClient] Template data is missing:', template);
      throw new HotelDetailsError('Template data is missing from response', 500, template);
    }

    // Fix media item types
    if (template.data.hotelMediaAtDetailsPage) {
      const { fixMediaItems } = await import('@/features/hotels/utils/fixMediaTypes');
      const fixed = fixMediaItems(template.data.hotelMediaAtDetailsPage);
      if (fixed) {
        template.data.hotelMediaAtDetailsPage = fixed;
      }
    }
    if (template.data.hotelDetailsMedia) {
      const { fixHotelGalleryMediaItems } = await import('@/features/hotels/utils/fixMediaTypes');
      const fixed = fixHotelGalleryMediaItems(template.data.hotelDetailsMedia);
      if (fixed) {
        template.data.hotelDetailsMedia = fixed;
      }
    }

    // logger.warn('[HotelDetailsClient] Received hotel details template:', template);

    return template;
  } catch (error) {
    if (error instanceof HotelDetailsError) {
      throw error;
    }

    // logger.error('[HotelDetailsClient] Request failed:', error);
    throw new HotelDetailsError('Failed to fetch hotel details', undefined, error);
  }
}
